import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";

import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

import { DeferredCommitFieldFrame } from "./DeferredCommitFieldFrame";
import {
  deferredCommitDirtyClassName,
  deferredCommitFlashClassName,
  deferredCommitOverlayGlyphPadding,
  useDeferredCommitFieldFeedback,
} from "./deferredCommitFieldShared";
import {
  DEFERRED_COMMIT_UNSAVED_HINT_DATE,
  deferredCommitStatusGlyphVisible,
} from "./DeferredCommitStatusGlyph";

/**
 * Native date pickers can fire `change` while the user is only changing month/year.
 * Keep a local draft and commit on blur so month navigation does not save mid-picker.
 */
export function DeferredCommitDateInput({
  value,
  disabled,
  pending,
  commitError,
  commitSucceeded,
  statusGlyphClassName,
  className,
  label,
  onCommit,
  onContextMenu,
}: {
  value: string;
  disabled: boolean;
  pending?: boolean;
  commitError?: string | null;
  commitSucceeded?: boolean;
  statusGlyphClassName?: string;
  className?: string;
  label?: ReactNode;
  onCommit: (value: string | null) => void;
  onContextMenu?: (event: MouseEvent<HTMLInputElement>, draft: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  const draftRef = useRef(value);
  const skipNextBlurCommitRef = useRef(false);

  const syncDraft = useCallback((next: string): void => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  useEffect(() => {
    syncDraft(value);
  }, [syncDraft, value]);

  const isDirty = useMemo(() => {
    const next = draft.trim() === "" ? null : draft.trim();
    const current = value.trim() === "" ? null : value.trim();
    return next !== current;
  }, [draft, value]);

  const flashTone = useDeferredCommitFieldFeedback(commitSucceeded, commitError);
  const showOverlayGlyph = deferredCommitStatusGlyphVisible({
    pending,
    isDirty,
    disabled,
    commitError,
    commitSucceeded,
  });

  const commitDraft = useCallback((): void => {
    if (pending === true) {
      return;
    }
    const draftNow = draftRef.current;
    const next = draftNow.trim() === "" ? null : draftNow.trim();
    const current = value.trim() === "" ? null : value.trim();
    if (next !== current) {
      onCommit(next);
    }
  }, [onCommit, pending, value]);

  return (
    <DeferredCommitFieldFrame
      label={label}
      pending={pending}
      isDirty={isDirty}
      disabled={disabled}
      commitError={commitError}
      commitSucceeded={commitSucceeded}
      unsavedHint={DEFERRED_COMMIT_UNSAVED_HINT_DATE}
      statusGlyphClassName={statusGlyphClassName}
      showOverlayGlyph={showOverlayGlyph}
    >
      <Input
        type="date"
        value={draft}
        disabled={disabled}
        aria-busy={pending === true}
        onChange={(event) => {
          syncDraft(event.target.value);
        }}
        onBlur={(event) => {
          if (skipNextBlurCommitRef.current) {
            skipNextBlurCommitRef.current = false;
            return;
          }
          syncDraft(event.currentTarget.value);
          commitDraft();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            skipNextBlurCommitRef.current = true;
            syncDraft(event.currentTarget.value);
            commitDraft();
            event.currentTarget.blur();
          }
        }}
        onContextMenu={(event) => {
          onContextMenu?.(event, draftRef.current);
        }}
        className={cn(
          "min-w-0 shrink-0",
          deferredCommitOverlayGlyphPadding(showOverlayGlyph && !label),
          className,
          pending === true && "opacity-70",
          deferredCommitDirtyClassName(isDirty, disabled),
          deferredCommitFlashClassName(flashTone),
        )}
      />
    </DeferredCommitFieldFrame>
  );
}
